import type { Ref, ShallowRef } from 'vue';
import { ref, shallowRef } from 'vue';
import { BrushComponent, DataZoomComponent, ToolboxComponent } from 'echarts/components';

import type { TChartWithZoom, TChartZoomInteractionMode } from './chartZoomToolbox';
import {
  applyDataZoomRange,
  calcZoomRangeByFactor,
  CHART_ZOOM_INSIDE_ID,
  FULL_ZOOM_RANGE,
  getInsideDataZoomRange,
  setDataZoomSelectActive,
} from './chartZoomToolbox';

export const ECHARTS_CHART_ZOOM_USE = [ToolboxComponent, DataZoomComponent, BrushComponent] as const;

export const ECHARTS_CHART_ZOOM_GRID_TOP = 48;

type TXAxisIndex = number | number[];

type TUseEChartsChartZoomOptions = {
  zoomFactor?: number;
  selectZoomOnReady?: boolean;
};

export type TEChartsChartZoomApi = {
  interactionMode: Ref<TChartZoomInteractionMode>;
  chartRef: ShallowRef<TChartWithZoom | null>;
  getZoomOption: (xAxisIndex?: TXAxisIndex) => Record<string, unknown>;
  bindChart: (chart: TChartWithZoom) => void;
  zoomIn: () => void;
  zoomOut: () => void;
  activateZoomSelectMode: () => void;
  activatePanMode: () => void;
  resetZoom: () => void;
};

/**
 * Для Options API (data + шаблон): Vue разворачивает Ref, из‑за этого
 * TEChartsChartZoomApi напрямую не проходит в prop — interactionMode ослаблен.
 */
export type TEChartsChartZoomInstanceApi = Omit<TEChartsChartZoomApi, 'interactionMode'> & {
  interactionMode: unknown;
};

export type TEChartsChartZoomToolbarApi = Pick<
  TEChartsChartZoomInstanceApi,
  'interactionMode' | 'zoomIn' | 'zoomOut' | 'activateZoomSelectMode' | 'activatePanMode' | 'resetZoom'
>;

/**
 * Кнопки — HTML (EChartsChartZoomToolbar).
 * dataZoom + скрытый toolbox — в Vue option (vue-echarts 8 не эмитит ready).
 * inside — wheel/pan; toolbox.dataZoom — движок режима «лупа».
 */
export const useEChartsChartZoom = (options: TUseEChartsChartZoomOptions = {}): TEChartsChartZoomApi => {
  const chartRef = shallowRef<TChartWithZoom | null>(null);
  const interactionMode = ref<TChartZoomInteractionMode>('zoom');
  const zoomFactor = options.zoomFactor ?? 0.7;
  const selectZoomOnReady = options.selectZoomOnReady ?? true;
  let isSelectModeBootstrapped = false;

  const getChart = (): TChartWithZoom | null => chartRef.value;

  const zoomIn = (): void => {
    const chart = getChart();
    if (!chart) return;
    applyDataZoomRange(chart, calcZoomRangeByFactor(getInsideDataZoomRange(chart), zoomFactor));
  };

  const zoomOut = (): void => {
    const chart = getChart();
    if (!chart) return;
    applyDataZoomRange(chart, calcZoomRangeByFactor(getInsideDataZoomRange(chart), 1 / zoomFactor));
  };

  const setInsideMoveOnMouseMove = (chart: TChartWithZoom, enabled: boolean): void => {
    chart.setOption({
      dataZoom: [
        {
          id: CHART_ZOOM_INSIDE_ID,
          moveOnMouseMove: enabled,
        },
      ],
    });
  };

  const activateZoomSelectMode = (): void => {
    interactionMode.value = 'zoom';
    const chart = getChart();
    if (!chart) return;
    setInsideMoveOnMouseMove(chart, false);
    setDataZoomSelectActive(chart, true);
  };

  const activatePanMode = (): void => {
    interactionMode.value = 'pan';
    const chart = getChart();
    if (!chart) return;
    setDataZoomSelectActive(chart, false);
    setInsideMoveOnMouseMove(chart, true);
  };

  const resetZoom = (): void => {
    const chart = getChart();
    if (!chart) return;
    applyDataZoomRange(chart, FULL_ZOOM_RANGE);
    activateZoomSelectMode();
  };

  const getZoomOption = (xAxisIndex: TXAxisIndex = 0) => ({
    animationDurationUpdate: 300,
    dataZoom: [
      {
        id: CHART_ZOOM_INSIDE_ID,
        type: 'inside',
        xAxisIndex,
        filterMode: 'none',
        zoomLock: true,
        zoomOnMouseWheel: false,
        // В режиме лупы drag = select-zoom; pan включаем только в activatePanMode
        moveOnMouseMove: false,
        moveOnMouseWheel: false,
      },
    ],

    toolbox: {
      show: true,
      right: -99999,
      top: -99999,
      itemSize: 0,
      feature: {
        dataZoom: {
          yAxisIndex: 'none',
          xAxisIndex,
          // Иначе toolbox создаёт select-dataZoom с filter → Y пересчитывается при зуме мышкой
          filterMode: 'none',
        },
      },
    },
  });

  const bindChart = (chart: TChartWithZoom): void => {
    chartRef.value = chart;

    if (isSelectModeBootstrapped) return;
    isSelectModeBootstrapped = true;

    if (!selectZoomOnReady) {
      activatePanMode();
      return;
    }

    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        activateZoomSelectMode();
      });
    });
  };

  return {
    interactionMode,
    chartRef,
    getZoomOption,
    bindChart,
    zoomIn,
    zoomOut,
    activateZoomSelectMode,
    activatePanMode,
    resetZoom,
  };
};
